import React, { useState, useRef, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";

const ZoomableImage = ({
  src,
  alt = "",
  loading = "eager",
  fetchpriority = "high",
  decoding = "async",
  style = {},
  onClick = null,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  // Check if image is already cached or complete on node mount
  const handleImageRef = useCallback((node) => {
    imgRef.current = node;
    if (node) {
      if (node.complete && node.naturalWidth > 0) {
        setImageLoaded(true);
        setImageError(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!src) {
      setImageLoaded(false);
      setImageError(true);
      return;
    }
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setImageLoaded(true);
      setImageError(false);
    } else {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [src]);

  // Safety fallback: ensure skeleton is dismissed if image is already loaded or takes too long
  useEffect(() => {
    if (imageLoaded || imageError) return;
    const timer = setTimeout(() => {
      if (imgRef.current) {
        if (imgRef.current.complete) {
          if (imgRef.current.naturalWidth > 0) {
            setImageLoaded(true);
          } else {
            setImageError(true);
          }
        } else {
          // Dismiss skeleton after fallback window so UI is never stuck
          setImageLoaded(true);
        }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [src, imageLoaded, imageError]);

  useEffect(() => {
    const checkDesktop = () => {
      if (typeof window !== "undefined") {
        const mq =
          typeof window.matchMedia === "function"
            ? window.matchMedia("(hover: hover)")
            : null;
        const hasHover = mq ? Boolean(mq.matches) : true;
        setIsDesktop(window.innerWidth >= 960 && hasHover);
      }
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <Box
      ref={containerRef}
      className="product-zoom-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "zoom-in",
        borderRadius: "14px",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Skeleton placeholder while image loads */}
      {!imageLoaded && !imageError && (
        <Skeleton
          data-testid="product-image-skeleton"
          variant="rectangular"
          animation="wave"
          sx={{
            width: "100%",
            height: "100%",
            minHeight: { xs: 340, sm: 480 },
            borderRadius: "14px",
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          }}
        />
      )}

      {/* Fallback if image fails to load */}
      {imageError && (
        <Box
          data-testid="product-image-error"
          sx={{
            width: "100%",
            height: "100%",
            minHeight: { xs: 340, sm: 480 },
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            color: "text.secondary",
          }}
        >
          <span>Image not available</span>
        </Box>
      )}

      <img
        ref={handleImageRef}
        src={src}
        alt={alt}
        loading={loading}
        fetchpriority={fetchpriority}
        decoding={decoding}
        onLoad={() => {
          setImageLoaded(true);
          setImageError(false);
        }}
        onError={() => {
          setImageError(true);
          setImageLoaded(false);
        }}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: "75vh",
          objectFit: "contain",
          userSelect: "none",
          opacity: imageLoaded ? 1 : 0,
          position: imageLoaded ? "relative" : "absolute",
          top: 0,
          left: 0,
          pointerEvents: imageLoaded ? "auto" : "none",
          transition: "opacity 0.2s ease-in-out",
          ...style,
        }}
      />

      {/* Expand Fullscreen / Separate Screen Button in Top Left */}
      {imageLoaded && (
        <Tooltip title="Open in separate screen">
          <IconButton
            aria-label="open zoom in separate screen"
            onClick={(e) => {
              e.stopPropagation();
              if (onClick) onClick();
            }}
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              backdropFilter: "blur(6px)",
              color: "#075e54",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              zIndex: 5,
              width: 38,
              height: 38,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#ffffff",
                transform: "scale(1.08)",
              },
            }}
          >
            <FullscreenIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Bottom Hint Badge */}
      {imageLoaded && (
        <Box
          className="product-zoom-hint-badge"
          sx={{
            position: "absolute",
            bottom: 14,
            right: 14,
            pointerEvents: "none",
            opacity: !isDesktop || isHovered ? 0.95 : 0,
            transform: !isDesktop || isHovered ? "scale(1)" : "scale(0.92)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
            backgroundColor: "rgba(255, 255, 255, 0.94)",
            backdropFilter: "blur(6px)",
            px: 1.25,
            py: 0.5,
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            color: "#075e54",
            fontSize: "0.75rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            zIndex: 3,
          }}
        >
          <FullscreenIcon sx={{ fontSize: 16 }} />
          <span>
            {isDesktop ? "Click to see full view" : "Tap to see full view"}
          </span>
        </Box>
      )}
    </Box>
  );
};

export default ZoomableImage;
